import React from 'react';
import { connect } from 'react-redux';
import {
  Dimensions, Text, View, StyleSheet, Platform, ScrollView,
} from 'react-native';
import { TabView } from 'react-native-tab-view';
import { buildQuantityString, MealIngredientCount } from 'fitbeat.models';

import InfoIcon from '../../assets/icons/info.svg';
import Theme from '../../theme';
import MealDetailedViewTabs from './mealDetailedViewTabs';
import { Icon, Heading, HeadingTypes } from '../../components';
import { defaultScale } from '../../utils/scaling';
import ServingsCounter from './ServingsCounter';

const { width } = Dimensions.get('window');
const isMobile = width <= 550;

type PlaceholderTypeWhenConvertedToTsx = any;

interface IProps {
  mealIngredients: MealIngredientCount[];
  shoppingListServingCount: number;
  currentTab: PlaceholderTypeWhenConvertedToTsx;
  // todo: add missing fields
}

interface IState {
  currentTab: PlaceholderTypeWhenConvertedToTsx;
  servingCount: number;
  availableTabs: string[];
  routes: PlaceholderTypeWhenConvertedToTsx[];
  index: PlaceholderTypeWhenConvertedToTsx;
  ingredientContainerHeight: number;
  instructionContainerHeight: number;
  // todo: add missing fields
}

class MealDetailedViewTabNavigator extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);
    const { currentTab, shoppingListServingCount } = this.props;
    const availableTabs = ['Ingredients', 'Instructions'];
    const routes: PlaceholderTypeWhenConvertedToTsx = [];
    availableTabs.forEach((tabName) => {
      routes.push({
        key: tabName,
        title: tabName,
      });
    });

    this.state = {
      availableTabs,
      routes,
      index: currentTab,
      ingredientContainerHeight: 500,
      instructionContainerHeight: 500,
      currentTab,
      servingCount: shoppingListServingCount,
    };
  }

  public handleIndexChange = (index) => {
    this.setState({ index });
    this.props.setActiveTab(index, this.state.routes[index]);
  };

  public renderHeader = (navigationProps) => {
    const { setActiveTab } = this.props;
    const { availableTabs } = this.state;

    return (
      <MealDetailedViewTabs
        availableTabs={availableTabs}
        setActiveTab={setActiveTab}
        currentTabIndex={this.state.index}
        jumpToIndex={(i) => {
          this.setState({ index: i });
        }}
        routes={navigationProps.navigationState.routes}
        activeTintColor={Theme.PRIMARY}
        inactiveTintColor={Theme.GREY}
        activeBackgroundColor={Theme.WHITE}
        inactiveBackgroundColor={Theme.WHITE}
      />
    );
  };

  public ingredientContainerOnLayout = (event) => {
    const { height } = event.nativeEvent.layout;
    const extraSpacing = 100;
    this.setState({ ingredientContainerHeight: height + extraSpacing });
  };

  public instructionContainerOnLayout = (event) => {
    const { height } = event.nativeEvent.layout;
    const extraSpacing = 80;
    this.setState({ instructionContainerHeight: height + extraSpacing });
  };

  public renderScene = ({ route }) => {
    const {
      ingredients, instructions, mealIngredients, tips, t, closeMealModalAndOpenSettings,
    } = this.props;
    const { servingCount } = this.state;
    const setServingCount = (newServingCount) => this.setState({ servingCount: newServingCount });

    const hasTips = tips && tips.length > 0;
    const ingredientsList = (
      <View style={styles.ingredientContainer}>
        {ingredients != null && ingredients.map((ingredient) => (
          <View key={ingredient.displayName} style={styles.ingredientItem}>
            <Icon
              size={defaultScale(25)}
              color={Theme.GREY}
              style={styles.arrowIcon}
              name='md-arrow-dropright'
            />
            <Text style={styles.ingredientText}>{ingredient.displayName}</Text>
          </View>
        ))}

        {mealIngredients != null && mealIngredients.map((mealIngredient) => (
          <View key={mealIngredient.ingredientId} style={styles.ingredientItem}>
            <Icon
              size={defaultScale(25)}
              color={Theme.GREY}
              style={styles.arrowIcon}
              name='md-arrow-dropright'
            />
            <Text style={styles.ingredientText}>{buildQuantityString(mealIngredient, servingCount)}</Text>
          </View>
        ))}
      </View>
    );
    const ingredientsContent = (
      <ScrollView>
        <View onLayout={this.ingredientContainerOnLayout}>
          <ServingsCounter
            servings={servingCount}
            onChanged={setServingCount}
            t={t}
          />
          {ingredientsList}
          {servingCount > 1 && (
            <View style={styles.warningMultipleServingsContainer}>
              <InfoIcon style={rawStyles.warningMultipleServingsIcon} />
              <Text style={styles.warningMultipleServingsText}>{t('servingSizesWarning')}</Text>
            </View>
          )}
          <View>
            <Text onPress={closeMealModalAndOpenSettings} style={styles.nuritionLink}>
              <Text style={styles.nutritionLinkText}>{t('learnMoreAboutTheNutritionScience')}</Text>
              <Text style={[styles.nutritionSourceLink, { color: Theme.PRIMARY }]}>
                {' '}
                {t('here')}
                .
              </Text>
            </Text>
          </View>
        </View>
      </ScrollView>
    );

    const instructionsContent = (
      <ScrollView>
        <View style={styles.instructionsContainer}>
          {instructions ? instructions.map((singleInstruction) => (
            <View key={singleInstruction.ingredientId} style={styles.listItem}>
              <Icon
                size={defaultScale(25)}
                color={Theme.GREY}
                style={styles.arrowIcon}
                name='md-arrow-dropright'
              />
              <Text style={styles.listText}>{singleInstruction}</Text>
            </View>
          )) : null}
          {hasTips
            && (
            <View style={styles.tipsContainer}>
              <Heading
                title={t('tips')}
                headerType={HeadingTypes.HEADING_ONE}
                style={styles.headerText}
              />
              {tips.map((singleTip) => (
                <View key={singleTip.displayName} style={styles.listItem}>
                  <Icon
                    size={defaultScale(25)}
                    color={Theme.GREY}
                    style={styles.arrowIcon}
                    name='md-arrow-dropright'
                  />
                  <Text style={styles.listText}>{singleTip}</Text>
                </View>
              ))}
            </View>
            )}
        </View>
      </ScrollView>
    );

    if (route.key === 'Ingredients') {
      return ingredientsContent;
    }
    return instructionsContent;
  };

  public render() {
    const height = this.state.ingredientContainerHeight > this.state.instructionContainerHeight
      ? this.state.ingredientContainerHeight
      : this.state.instructionContainerHeight;
    return (
      <TabView
        initialLayout={{
          width: Dimensions.get('window').width,
          height: defaultScale(height),
        }}
        navigationState={this.state}
        renderScene={this.renderScene}
        renderTabBar={this.renderHeader}
        onIndexChange={this.handleIndexChange}
        style={{ height: defaultScale(height) }}
      />
    );
  }
}

const rawStyles = {
  warningMultipleServingsIcon: {
    margingHorizontal: defaultScale(7),
    minWidth: 19,
  },
};

const styles = StyleSheet.create({
  nuritionLink: {
    paddingHorizontal: 15,
  },
  warningMultipleServingsContainer: {
    paddingHorizontal: defaultScale(25),
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 15,
  },
  warningMultipleServingsText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
    paddingHorizontal: defaultScale(8),
  },
  ingredientContainer: {
    flexDirection: 'column',
    paddingTop: defaultScale(10),
    marginBottom: defaultScale(10),
    ...Platform.select({
      web: {
        paddingTop: 'auto',
        marginBottom: 'auto',
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        maxHeight: isMobile ? 'auto' : 300,
      },
    }),
    paddingHorizontal: defaultScale(10),
  },
  instructionsContainer: {
    paddingTop: defaultScale(10),
    ...Platform.select({
      web: {
        paddingTop: 'auto',
      },
    }),
    paddingHorizontal: defaultScale(10),
  },
  instructions: {
    ...Theme.textStyle.bodyCopyReg,
    paddingHorizontal: defaultScale(15),
    color: Theme.CHARCOAL,
  },
  ingredientItem: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
  },
  ingredientText: {
    ...Theme.textStyle.bodyCopyReg,
    paddingVertical: defaultScale(2),
    color: Theme.TEXT_DARK,
    paddingHorizontal: defaultScale(10),
  },
  listItem: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
  },
  tips: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.CHARCOAL,
  },
  listText: {
    ...Theme.textStyle.bodyCopyReg,
    alignSelf: 'center',
    color: Theme.CHARCOAL,
    paddingHorizontal: defaultScale(15),
  },
  arrowIcon: {
    paddingHorizontal: 5,
  },
  nutritionLink: {
    paddingVertical: defaultScale(10),
    paddingLeft: defaultScale(18),
  },
  nutritionLinkText: {
    ...Theme.textStyle.bodyCopyReg,
  },
  nutritionSourceLink: {
    ...Theme.textStyle.bodyCopyReg,
  },
  tipsContainer: {
    paddingVertical: defaultScale(10),
  },
  headerText: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.DARK_CHARCOAL,
    justifyContent: 'center',
    paddingHorizontal: defaultScale(15),
    paddingVertical: defaultScale(10),
  },
});

export default connect()(MealDetailedViewTabNavigator);
